<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import useLocale from '../../../composables/useLocale';
import type { StepperValueLabelMapping } from '../../../search-panel-types';

type Emits = {
  (e: 'update:modelValue', value: number): void;
  (e: 'change'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: number;
  min: number;
  max: number;
  valueLabelMappings: StepperValueLabelMapping[];
  ariaLabel: string;
};
const props = defineProps<Props>();

const { t } = useLocale();

const valueToLabel = computed(() => new Map(props.valueLabelMappings.map((mapping) => [mapping.value, mapping.label])));
const labelToValue = computed(
  () => new Map(props.valueLabelMappings.map((mapping) => [mapping.label.trim().toLowerCase(), mapping.value])),
);

const formatValue = (value: number): string => valueToLabel.value.get(value) ?? '';

const parseInputText = (text: string): number | null => labelToValue.value.get(text.trim().toLowerCase()) ?? null;

const inputText = ref(formatValue(props.modelValue));

watch(
  () => props.modelValue,
  (value) => {
    inputText.value = formatValue(value);
  },
);

const decreaseLabel = `${t('content_search.label_decrease')} ${props.ariaLabel}`;
const increaseLabel = `${t('content_search.label_increase')} ${props.ariaLabel}`;

const decrement = () => {
  if (props.modelValue <= props.min) {
    return;
  }
  emit('update:modelValue', props.modelValue - 1);
  emit('change');
};

const increment = () => {
  if (props.modelValue >= props.max) {
    return;
  }
  emit('update:modelValue', props.modelValue + 1);
  emit('change');
};

const goToMin = () => {
  emit('update:modelValue', props.min);
  emit('change');
};

const goToMax = () => {
  emit('update:modelValue', props.max);
  emit('change');
};

const onKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      increment();
      break;
    case 'ArrowDown':
      event.preventDefault();
      decrement();
      break;
    case 'Home':
      event.preventDefault();
      goToMin();
      break;
    case 'End':
      event.preventDefault();
      goToMax();
      break;
    case 'Enter':
      event.preventDefault();
      commitInputText();
      break;
    default:
      break;
  }
};

const commitInputText = () => {
  const parsed = parseInputText(inputText.value);
  if (parsed !== null && parsed >= props.min && parsed <= props.max && parsed !== props.modelValue) {
    emit('update:modelValue', parsed);
    emit('change');
    return;
  }
  inputText.value = formatValue(props.modelValue);
};
</script>

<template>
  <div class="formatted-stepper">
    <v-text-field
      v-model="inputText"
      density="compact"
      variant="outlined"
      bg-color="surface-bright"
      color="primary"
      hide-details
      class="v-number-input v-number-input--split number-inputs"
      :aria-label="ariaLabel"
      @keydown="onKeydown"
      @blur="commitInputText"
    >
      <template #prepend-inner>
        <div class="v-number-input__control">
          <v-btn
            icon="mdi-minus"
            flat
            height="100%"
            size="default"
            :disabled="modelValue <= min"
            :aria-label="decreaseLabel"
            tabindex="-1"
            aria-hidden="true"
            @click="decrement"
          />
          <v-divider vertical />
        </div>
      </template>
      <template #append-inner>
        <div class="v-number-input__control">
          <v-divider vertical />
          <v-btn
            icon="mdi-plus"
            flat
            height="100%"
            size="default"
            :disabled="modelValue >= max"
            :aria-label="increaseLabel"
            tabindex="-1"
            aria-hidden="true"
            @click="increment"
          />
        </div>
      </template>
    </v-text-field>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.number-inputs {
  max-height: 40px;
  :deep(.v-field__input) {
    text-align: center !important;
    font-size: 0.875rem !important;
  }

  :deep(.v-btn) {
    color: rgb(var(--v-theme-on-surface)) !important;
    background-color: transparent !important;
  }

  :deep(.v-btn[disabled]) {
    opacity: 0.26 !important; /* Apply opacity when button is disabled */
  }

  @media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height)) !important;
    }
  }

  @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) + 12px) !important;
    }
  }

  @media (max-width: 400px) {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) - 12px) !important;
    }
  }
}
</style>
